import React, { useMemo, useState } from "react";
import Sidebar from "../../components/Sidebar";
import BottomNav from "../../components/BottomNav";

const SUPPORT = {
  email: "support@ngcfinancialservices.com",
  email2: "info@ngcfinancialservices.com",
  tel: "+1 800 451 4210",
  whatsapp1: "+237 698766260",
  whatsapp2: "+237 682856750",
  whatsapp3: "+1 3433003495"
};

const FAQS = [
  // Compte & KYC
  {
    id: "kyc-1",
    category: "Compte & Vérification",
    q: "Comment vérifier mon compte (KYC) ?",
    a: "Allez dans Profil > Vérification. Téléversez une pièce d’identité valide et un justificatif. La vérification peut prendre de quelques minutes à 48h selon le volume.",
  },
  {
    id: "kyc-2",
    category: "Compte & Vérification",
    q: "Pourquoi ma vérification a été refusée ?",
    a: "Les causes fréquentes : photo floue, document expiré, informations non lisibles, ou incohérence entre nom et document. Reprenez des photos nettes et assurez-vous que les infos correspondent.",
  },
  {
    id: "acct-1",
    category: "Compte & Sécurité",
    q: "J’ai oublié mon mot de passe, que faire ?",
    a: "Utilisez la fonction “Mot de passe oublié” à l’écran de connexion. Un lien/OTP vous sera envoyé pour réinitialiser votre mot de passe.",
  },
  {
    id: "acct-2",
    category: "Compte & Sécurité",
    q: "Comment sécuriser mon compte ?",
    a: "Utilisez un mot de passe fort, évitez de partager votre OTP, et déconnectez-vous des appareils publics. Si tu as une option 2FA, active-la dès que possible.",
  },

  // Transactions
  {
    id: "tx-1",
    category: "Transactions",
    q: "Quels sont les statuts d’une transaction ?",
    a: "En cours (pending) : paiement/traitement en attente. Complété (completed) : envoyé avec succès. Annulé (cancelled) : stoppé avant traitement. Échec (failed) : opération non finalisée.",
  },
  {
    id: "tx-2",
    category: "Transactions",
    q: "Je vois “En cours” depuis longtemps, que faire ?",
    a: "Vérifiez que le paiement a bien été effectué. Si c’est le cas, contactez le support avec votre référence de transaction et une preuve de paiement.",
  },
  {
    id: "tx-3",
    category: "Transactions",
    q: "Puis-je annuler une transaction ?",
    a: "Oui, uniquement si elle est encore “En cours (pending)” et non traitée. Une fois complétée, l’annulation n’est généralement plus possible.",
  },
  {
    id: "tx-4",
    category: "Transactions",
    q: "Comment ajouter une note à une transaction ?",
    a: "Dans Historique, ouvre le menu Actions sur la transaction, puis “Ajouter/Modifier note”. Cela n’affecte pas le transfert, c’est juste un mémo personnel.",
  },

  // Paiements & frais
  {
    id: "pay-1",
    category: "Paiement & Frais",
    q: "Quels sont les frais appliqués ?",
    a: "Les frais dépendent du corridor, du mode et du montant. L’application affiche toujours les frais et le total avant validation.",
  },
  {
    id: "pay-2",
    category: "Paiement & Frais",
    q: "Le paiement Mobile Money a échoué, que faire ?",
    a: "Assurez-vous d’avoir un solde suffisant et que votre opérateur est disponible. Réessayez après quelques minutes. Si le débit a eu lieu sans confirmation, contactez le support.",
  },

  // Délais & bénéficiaires
  {
    id: "del-1",
    category: "Délais & Bénéficiaires",
    q: "Quels sont les délais de réception ?",
    a: "Les délais varient selon le pays, le réseau et la méthode. En général, la réception peut aller de quelques minutes à 48h.",
  },
  {
    id: "ben-1",
    category: "Délais & Bénéficiaires",
    q: "J’ai renseigné un mauvais numéro/email de bénéficiaire, que faire ?",
    a: "Si la transaction est encore en “pending”, annule-la immédiatement puis recrée-la. Si elle est déjà complétée, contacte le support au plus vite avec la référence.",
  },

  // Conformité
  {
    id: "cmp-1",
    category: "Conformité",
    q: "Pourquoi dois-je fournir mes informations personnelles ?",
    a: "Pour respecter les exigences de conformité (KYC/AML), prévenir la fraude, et sécuriser les transferts. Les données sont traitées selon la politique de confidentialité.",
  },
];

function Card({ title, desc, href, cta }) {
  return (
    <a
      href={href}
      className="block rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5
                 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition"
    >
      <div className="text-sm font-semibold text-slate-900 dark:text-white">{title}</div>
      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{desc}</div>
      <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
        {cta} <span aria-hidden>→</span>
      </div>
    </a>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 bg-white dark:bg-slate-900 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="text-sm font-semibold text-slate-900 dark:text-white">{q}</div>
          <div className="text-slate-400">{open ? "−" : "+"}</div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 bg-white dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-300">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function UserSupportPage() {
  const waText = encodeURIComponent("Bonjour, j’ai besoin d’assistance sur NGC Financial Services.");
  const waLink = (num) => {
    const normalized = num.replace(/\s+/g, "").replace("+", "");
    return `https://wa.me/${normalized}?text=${waText}`;
  };

  const categories = useMemo(() => {
    const set = new Set(FAQS.map((f) => f.category));
    return ["Toutes", ...Array.from(set)];
  }, []);

  const [cat, setCat] = useState("Toutes");
  const [q, setQ] = useState("");

  const filteredFaqs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return FAQS.filter((f) => {
      const inCat = cat === "Toutes" || f.category === cat;
      const inSearch =
        !needle ||
        f.q.toLowerCase().includes(needle) ||
        f.a.toLowerCase().includes(needle);
      return inCat && inSearch;
    });
  }, [cat, q]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex">
        <Sidebar />

        <main className="flex-1 pb-28 md:pb-8">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Support
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Contactez notre équipe ou consultez les questions fréquentes.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card title="WhatsApp (Service client)" desc={SUPPORT.whatsapp1} href={waLink(SUPPORT.whatsapp1)} cta="Ouvrir WhatsApp" />
              <Card title="WhatsApp (Service client)" desc={SUPPORT.whatsapp2} href={waLink(SUPPORT.whatsapp2)} cta="Ouvrir WhatsApp" />
              <Card title="Téléphone" desc={SUPPORT.tel} href={`tel:${SUPPORT.tel.replace(/\s+/g, "")}`} cta="Appeler" />
              <Card title="Email" desc={SUPPORT.email2} href={`mailto:${SUPPORT.email2}`} cta="Envoyer un email" />
              <Card title="Email" desc={SUPPORT.email} href={`mailto:${SUPPORT.email}`} cta="Envoyer un email" />
              <Card title="WhatsApp (Service client)" desc={SUPPORT.whatsapp3} href={waLink(SUPPORT.whatsapp3)} cta="Ouvrir WhatsApp" />
            </div>

            {/* FAQ */}
            <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5 space-y-4">
              <div className="flex items-start justify-between gap-3 flex-col sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-white">FAQ</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Trouvez rapidement une réponse sans contacter le support.
                  </p>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <select
                    value={cat}
                    onChange={(e) => setCat(e.target.value)}
                    className="w-full sm:w-52 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Rechercher…"
                    className="w-full sm:w-56 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                {filteredFaqs.length === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Aucun résultat. Essayez une autre recherche.
                  </div>
                ) : (
                  filteredFaqs.map((f) => <FaqItem key={f.id} q={f.q} a={f.a} />)
                )}
              </div>

              <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4">
                <p className="text-sm text-slate-700 dark:text-slate-200">
                  Toujours bloqué ? Indiquez votre email, numéro et la référence de transaction (si applicable).
                </p>
              </div>
            </section>
          </div>

          <div className="md:hidden fixed bottom-0 left-0 w-full z-50">
            <BottomNav />
          </div>
        </main>
      </div>
    </div>
  );
}
